import { APP } from '@/shared/config'
import { client } from './generated/client.gen'
import { API_NOT_JSON, parseProblem } from './problem'
import { newRequestId, setLastRequestId } from './request-id'

/** В контракте нет ни одной ручки, отвечающей не-JSON. Проверено грепом по `openapi/paths`. */
const JSON_CONTENT_TYPE = /^application\/(problem\+)?json\b/i

/**
 * Подменяем «успешный» не-JSON ответ на честную ошибку.
 *
 * Тело настоящего ответа не читаем и не показываем: это почти наверняка наш же `index.html`,
 * и вываливать его в отчёт об ошибке незачем — важен сам факт и content-type.
 */
function notJsonProblem(response: Response, contentType: string): Response {
  const body = {
    type: API_NOT_JSON,
    title: 'Сервер ответил не тем',
    status: 502,
    detail:
      `Ожидался JSON, пришёл «${contentType || 'ответ без Content-Type'}» ` +
      `(HTTP ${response.status}, ${response.url}). Обычно это значит, что /api не проксируется ` +
      `на gateway: SPA-фолбэк раздачи отдаёт index.html на любой путь, включая /api/*.`,
  }

  return new Response(JSON.stringify(body), {
    status: 502,
    headers: {
      'Content-Type': 'application/problem+json',
      // Свой id не выдумываем: сохраняем тот, что ушёл в запросе, — по нему ищут в логах шлюза.
      'X-Request-Id': response.headers.get('X-Request-Id') ?? '',
    },
  })
}

/**
 * Настройка сгенерированного клиента. Вызывается один раз при старте приложения.
 * onUnauthorized — реакция на 401: чистка кеша и уход на /login.
 */
export function setupHttp(onUnauthorized: () => void): void {
  client.setConfig({
    baseUrl: APP.apiBase,
    // Один домен со шлюзом: куки прикрепляются сами, CORS не нужен.
    credentials: 'same-origin',
  })

  client.interceptors.request.use((request) => {
    request.headers.set('X-Request-Id', newRequestId())
    return request
  })

  client.interceptors.response.use((response, request) => {
    const requestId =
      response.headers.get('X-Request-Id') ?? request.headers.get('X-Request-Id') ?? null

    if (response.ok) {
      // 2xx — ещё не значит «данные». Если /api не проксируется на gateway, SPA-фолбэк раздачи
      // (`try_files $uri $uri/ /index.html`) отдаёт index.html НА ЛЮБОЙ путь, включая /api/me:
      // приходит честный 200 с телом text/html.
      //
      // Клиент разбирает тело по Content-Type: на text/html он парсит его как ТЕКСТ, и в data
      // ложится непустая строка — то есть значение, истинное по truthiness. Дальше всё ломается
      // тихо и по-разному: страница входа решает, что пользователь уже вошёл, и уходит на
      // дашборд мимо формы; список броней получает строку вместо массива.
      //
      // Ни линтер, ни typecheck этого не увидят: типы обещают Session, а приезжает string.
      // Поэтому проверяем форму на входе — один раз здесь, на все 80 ручек контракта.
      const isEmpty = response.status === 204 || response.headers.get('Content-Length') === '0'
      const contentType = response.headers.get('Content-Type') ?? ''

      if (!isEmpty && !JSON_CONTENT_TYPE.test(contentType)) {
        setLastRequestId(requestId)
        return notJsonProblem(response, contentType)
      }

      return response
    }

    setLastRequestId(requestId)

    if (response.status === 401) onUnauthorized()

    return response
  })

  // Ошибка приезжает как тело не-2xx ответа (уже распарсенный JSON либо сырой текст) — приводим
  // её к Problem, чтобы весь остальной код работал с одним типом, а не с четырьмя формами ошибки.
  //
  // response здесь может быть undefined: это настоящий сетевой сбой (offline, DNS, оборванное
  // соединение) — fetch бросил ДО того, как появился HTTP-ответ, а не после. В этом случае Problem
  // намеренно не строим и отдаём ошибку как есть: isProblem(error) останется false, и shouldRetry
  // (app/providers/query-client.ts) распознает её как сетевой сбой, а не как 4xx, и включит ретрай.
  client.interceptors.error.use((error, response, request) => {
    if (!response) {
      // X-Request-Id надо сохранить и здесь, а не только в перехватчике ответа.
      //
      // При сетевом сбое перехватчик ответа не выполняется вовсе: fetch бросил до того, как
      // появился Response. Без этой строки на «экране смерти» пользователь увидел бы либо пустой
      // id, либо — что хуже — чужой id от предыдущей, никак не связанной ошибки, и саппорт пошёл
      // бы искать в логах шлюза не тот запрос.
      //
      // Заголовок к этому моменту уже стоит: его ставит перехватчик запроса, то есть ДО fetch.
      setLastRequestId(request?.headers.get('X-Request-Id') ?? null)
      return error
    }
    return parseProblem(error, response.status)
  })
}
